<template>
  <div
    class="design-element-sync-chip-anchor"
    :class="{ 'design-element-sync-chip-anchor--column': column }"
    v-if="badge || stored"
  >
    <fts-tippy v-if="badge">
      <span
        class="design-element-sync-chip"
        :class="'design-element-sync-chip--' + badge.state"
      >{{ badge.label }}</span>
      <template #content>
        <div class="tippy-hint sync-chip-tip">
          <div class="tippy-hint-highlight">{{ badge.title }}</div>
          <div v-if="badge.note">{{ badge.note }}</div>
          <div class="sync-chip-tip-reasons-note" v-if="badge.reasonsNote">
            {{ badge.reasonsNote }}
          </div>
          <div class="sync-chip-tip-reason" v-for="(reason, i) in badge.reasons" :key="i">
            <span
              class="sync-chip-tip-origin"
              :class="'sync-chip-tip-origin--' + reason.origin"
            >{{ reason.origin }}</span>
            <span class="sync-chip-tip-what">{{ reason.what }}</span>
            <span class="sync-chip-tip-kind">{{ reason.kind }}</span>
          </div>
        </div>
      </template>
    </fts-tippy>
    <!-- computed field showing its STORED value only (off-form dependencies): sits beside the
         sync chip; the host computes the notice (it owns the form + information context) -->
    <fts-tippy v-if="stored">
      <span class="design-element-sync-chip design-element-sync-chip--stored">{{ stored.label }}</span>
      <template #content>
        <div class="tippy-hint sync-chip-tip">{{ stored.tip }}</div>
      </template>
    </fts-tippy>
  </div>
</template>

<script>
import FTSTippy from './FTSTippy';

/* Human names for the formula properties the sync classifier reports
   (FieldTrigger.FormulaTypes, api/canvastest/synctriggers): "formula" is the target field's
   VALUE, the other four are its CONTROL formulas. An unmapped/absent kind renders bare — the
   endpoint sends no types when the target model came from the server's Tier-2 cache, and that
   means "unknown", not "no control formula". */
const SYNC_FORMULA_KINDS = {
  formula: "value",
  visibleFormula: "visibility",
  requiredFormula: "required",
  readOnlyFormula: "read-only",
  validateFormula: "validation"
};

/**
 * Per-field sync indicator for the form designer — the design-time twin of the runtime trigger
 * model: does editing this input round-trip to the server, and WHICH origin raises it (a field
 * formula, naming value vs control; the form automation; or a backend script).
 * View-only and formula-derived fields keep the chip but drop the "editing" frame — their
 * changes (not the user's keystrokes) are what sync.
 *
 * Renders nothing outside the form designer: `formSyncIndicator` is only provided there.
 */
export default {
  components: {
    "fts-tippy": FTSTippy
  },
  props: {
    /* the design element this chip belongs to (a field, or a table column) */
    el: { type: Object, default: null },
    /* table-column placement (tighter inset) */
    column: Boolean,
    /* document designer has no sync model — suppress entirely */
    disabled: Boolean,
    /* { label, tip } notice for a computed field whose formula reads off-form fields — it can
       only show its stored value while editing. Computed by the host (FTSDesignElement), which
       owns the form/information context this component deliberately doesn't. */
    stored: { type: Object, default: null }
  },
  inject: {
    /* { verdict } from FTSFormDesigner, refreshed for the whole form on any change */
    formSyncIndicator: { default: null }
  },
  computed: {
    badge() {
      const holder = this.formSyncIndicator;
      const verdict = holder && holder.verdict;
      const el = this.el;
      if (!verdict || this.disabled || !el || !el.id || !el.fieldName) return null;

      /* One row per reason, each naming WHICH origin raised it: a server-owned formula target
         (with the formula kinds that read this input — value vs control), or the form automation
         reading it (synthetic target, origin "automation"). Built for the ALWAYS-sync case too —
         the form-wide origin says why every edit posts, these say what this field itself feeds. */
      const entry = (verdict.fields || []).find(f => String(f.id) === String(el.id));
      const targets = (entry && entry.targets) || [];
      const reasons = targets
        .filter(t => t.origin !== "automation" && t.origin !== "script")
        .map(t => {
          const kinds = (t.formulaTypes || [])
            .map(k => SYNC_FORMULA_KINDS[k] || k)
            .join(", ");
          return {
            origin: "formula",
            what: t.secondary ? `${t.primary} › ${t.secondary}` : t.primary,
            kind: kinds ? `${kinds} formula` : "formula"
          };
        });
      if (targets.some(t => t.origin === "automation")) {
        reasons.push({
          origin: "automation",
          what: "Form automation",
          kind: "reads this field"
        });
      }
      /* Mechanism B (declared triggers): the backend script re-runs when this field changes */
      if (targets.some(t => t.origin === "script")) {
        reasons.push({
          origin: "script",
          what: "Backend script",
          kind: "declared trigger field"
        });
      }

      if (verdict.always && verdict.always.length) {
        const names = { script: "a backend script", automation: "an unanalyzable automation" };
        const what = verdict.always.map(o => names[o] || o).join(" and ");
        /* D-3 reason strings from the classifier — which lever would stop the every-edit sync
           (e.g. "script (no declared trigger fields)", the automation diagnostic) */
        const why = (verdict.alwaysReasons || []).join("; ");
        return {
          state: "always",
          label: "sync",
          title: "Every edit syncs",
          /* the form-wide origin is not this field's doing — say so plainly, then list what IS */
          note: `This form has ${what}, so the server runs on every change.`
            + (why ? ` (${why})` : "")
            + " Form-wide — not caused by this field.",
          reasonsNote: reasons.length ? "This field also feeds:" : null,
          reasons
        };
      }

      /* Nothing is triggered by this field -> no chip. The indicator flags a COST; a field that
         costs nothing needs no badge, and silence reads better than a wall of green "local" tags.
         It also stops the chip having to assert where the field's formula runs, which is a
         different question it was never given the data to answer (SS05941: a visibility formula
         reading Entry is resolved server-side, yet the field is nobody's trigger input, so the
         old "local" label was placed on a field whose formula is not local at all). */
      if (!entry) return null;

      /* Statically non-editable (view only / formula-derived): nobody types into it, so
         "editing" is the wrong frame — yet its membership in the trigger set is real: the
         value still CHANGES (client compute, the server), and that change is what
         round-trips. Say so instead of hiding the cost. readOnlyFormula keeps the editing
         wording — conditionally read-only fields are sometimes editable. */
      const nonEditable = !!(el.readOnly || el.computed || el.formulaMode);

      return {
        state: "sync",
        label: "sync",
        title: nonEditable ? "Changes to this field sync" : "Editing this field syncs",
        note: nonEditable
          ? "It can't be edited directly — formulas or the server still change it, and that change syncs."
          : reasons.length ? null : "Server work depends on it.",
        reasonsNote: reasons.length ? "Server work that depends on it:" : null,
        reasons
      };
    }
  }
};
</script>
